<template>
  <div
    class="video-player critical-component-clear"
    :class="{
      'video-player--padding': paddingBottom,
      'video-player--hide-controls': controlsConfig.hide,
      [$class('active')]: videoPlayback.state === 'playing',
    }"
    :style="{ paddingBottom }"
  >
    <div class="video-player__controls">
      <button
        v-if="controlsConfig.playPause"
        class="video-player__control"
        type="button"
        @click="handleControlClick"
      >
        <span
          class="visually-hidden"
          v-text="controlText"
        />

        <component :is="icon" />
      </button>

      <button
        v-if="subtitles"
        class="video-player__subtitles video-player__control"
        :class="{ [$class('active')]: displaySubtitles }"
        type="button"
        @click="displaySubtitles = !displaySubtitles"
      >
        <span
          class="visually-hidden"
          v-text="subtitlesText"
        />

        <span
          class="text-other-label-s"
          aria-hidden="true"
          v-text="$string('accessibility.video.cc')"
        />
      </button>

      <range-slider
        v-if="videoPlayback.state !== 'loading' && hasProgressBar"
        :id="controlsConfig.id"
        v-model="videoPlayback.time"
        class="video-player__progress-bar"
        :config="rangeSliderConfig"
        :fallback-inputs="false"
        :label="$string('accessibility.video.progress_bar')"
        :loading="videoPlayback.state === 'loading'"
        @keydown.left.right="handleProgressBarInteraction"
        @keydown.space.enter="handleControlClick"
        @mousedown="handleProgressBarInteraction"
        @touchdown="handleProgressBarInteraction"
        @update-value="handleProgressBarUpdate"
      >
        <template #tooltipText="{ value }">
          <span v-text="formatProgressBarTooltip(value)" />
        </template>
      </range-slider>
    </div>

    <button
      class="video-player__overlay"
      tabindex="-1"
      type="button"
      @click="handleControlClick"
    >
      <span
        class="visually-hidden"
        v-text="$string('accessibility.video.play')"
      />

      <transition name="fade">
        <responsive-image
          v-if="displayOverlay && (image?.src || image?.url)"
          class="video-player__image"
          :alt="false"
          :image="image"
          :lazyload="lazyload"
          :loaded-animation="loadedAnimation"
          :padding="false"
          :ratio="ratio"
          :sizes="sizes"
        />
      </transition>
    </button>

    <video
      ref="video"
      v-bind="attributesBind"
      class="video-player__video"
      :poster="videoPoster"
      @canplay="handleVideoUpdate('ready')"
      @canplaythrough="handleVideoUpdate('ready')"
      @ended="handleVideoEnd"
      @loadedmetadata="handleVideoUpdate('ready')"
      @pause="videoPlayback.state = 'paused'"
      @play="videoPlayback.state = 'playing'"
      @timeupdate="handleVideoUpdate('playing')"
    >
      <source
        v-for="(source) of videoSources"
        :key="source.url"
        :src="source.url"
        :type="source.mimeType"
      >

      <track
        v-if="subtitles"
        kind="captions"
        :label="$variable('store.languageName')"
        :src="subtitles"
        :srclang="$variable('store.language')"
      >
    </video>
  </div>
</template>

<script>

/**
 * Vue: Video player (video-player)
 * -----------------------------------------------------------------------------
 * Video player functionality.
 *
 * @param {Object} [attributes] - Video element attributes overrides.
 * @param {Object} [controls] - Control config overrides, subtitles are
 * controlled by the separate subtitles prop.
 * @param {Boolean} [controls.hide] - Hide controls, show on hover.
 * @param {Boolean} [controls.id] - Progress bar input ID, required for progress
 * bar to be shown.
 * @param {Boolean} [controls.playPause] - Display play/pause control.
 * @param {Boolean} [controls.progressBar] - Display progress bar.
 * @param {Object} [image] - Shopify image object.
 * @param {Object} [intersectionObserver] - Automatically play video when it
 * scrolls into view, and pause when it leaves view, overrides isActive and
 * autoplay attributes on video load.
 * @param {Boolean} [intersectionObserver.enable] - Enables the observer.
 * @param {String} [intersectionObserver.rootMargin] - Grow or shrink element's
 * bounding box before detecting intersections.
 * @param {Number|Array} [intersectionObserver.threshold] - Sets at what
 * percentage of the element's visibility the callback is executed.
 * @param {Boolean} [isActive] - Video is active (play/pause based on state).
 * @param {Boolean} [lazyload] - Lazyload image, defaults to true.
 * @param {Boolean} [loadedAnimation] - Applies image loaded animation to the
 * responsive image component.
 * @param {String} [parentState] - Parent state to control playback, set to
 * `playing` to play the video, all other states pause the video.
 * @param {Boolean|Number|String} [ratio] - Aspect ratio of video in
 * `width:height`, `width / height`, or decimal ratio format (width/height),
 * will use the ratio of the image if set to 'image', will use the original
 * ratio of video if not set or set to false.
 * @param {Strong} [sizes] - Value for image sizes attribute, if an image is
 * passed then this is required.
 * @param {Array} sources - Array of video object sources.
 * @param {String} [subtitles] - Video subtitles/captions .vtt file URL.
 * @param {Object} [video] - Formatted media video object - deprecated.
 *
 * @emits videoEnd - Emit video playback ended.
 * @emits videoStateUpdate - Emit new video playback state.
 * @emits videoTimeUpdate - Emit new video time.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'

import ResponsiveImage from '~global/images/responsive-image'

import IconLoading from '~icons/general/loading.svg'
import IconPause from '~icons/general/pause.svg'
import IconPlay from '~icons/general/play.svg'

import { setIntersectionObserver } from '~/helpers/general'
import { computeAspectRatio, computePaddingBottom, validateRatio } from '~/helpers/vue'

export default {
  name: 'VideoPlayer',

  components: {
    IconLoading,
    IconPause,
    IconPlay,
    RangeSlider: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.range-slider' */
        '~async/utils/range-slider/range-slider'
      ),
    }),
    ResponsiveImage,
  },

  props: {
    attributes: {
      type: Object,
      default: () => ({}),
    },
    controls: {
      type: Object,
      default: () => ({}),
      validator: (object) => {
        if (!Object.keys(object).length) {
          return true
        }

        const allValid = Object.entries(object).every(([key, value]) => {
          return key === 'id'
            ? typeof value === 'string'
            : typeof value === 'boolean'
        })

        if (allValid && object.progressBar) {
          return Boolean(object.id)
        }

        return allValid
      },
    },
    image: {
      type: Object,
      default: () => ({
        alt: '',
        height: 0,
        src: '',
        width: 0,
      }),
    },
    intersectionObserver: {
      type: Object,
      default: () => ({
        enable: false,
        rootMargin: '0px',
        threshold: 0.1,
      }),
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    loadedAnimation: {
      type: Boolean,
      default: false,
    },
    parentState: {
      type: String,
      default: '',
    },
    ratio: {
      type: [Boolean, Number, String],
      default: false,
      validator: validateRatio,
    },
    sizes: {
      type: String,
      default: null,
    },
    sources: {
      type: Array,
      default: () => [],
      required: true,
    },
    subtitles: {
      type: String,
      default: '',
    },
    url: {
      type: String,
      default: '',
    },
    video: {
      type: Object,
      default: () => ({
        format: 'mp4',
        mimeType: 'video/mp4',
        url: '',
      }),
    },
  },

  emits: {
    'video-end': () => true,
    'video-state-update': (payload) => {
      return ['loading', 'paused', 'playing', 'ready'].includes(payload)
    },
    'video-time-update': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      displayOverlay: true,
      displaySubtitles: false,
      overlayPaused: false,
      videoPlayback: {
        duration: 0,
        state: 'loading',
        time: 0,
        updateTime: true,
        wasPlaying: false,
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Compute aspect ratio for video and image poster.
     * - If no ratio is set, or if the ratio is set to 'image' but no image is
     *   provided, returns `null` to use the video's default aspect ratio.
     * - Otherwise uses the `computeAspectRatio` helper to compute a valid
     *   aspect ratio.
     * @returns {String|Null}
     */
    aspectRatio() {
      if (!this.ratio || (this.ratio === 'image' && !this.image)) {
        return null
      }

      return computeAspectRatio(this.ratio, this.image)
    },

    /**
     * Compute attributes.
     * @returns {Object}
     */
    attributesBind() {
      const defaults = {
        controlslist: 'nofullscreen',
        crossorigin: 'anonymous',
        disablepictureinpicture: true,
        loop: true,
        muted: true,
        playsinline: true,
        preload: false,
      }

      return {
        ...defaults,
        ...this.attributes,
      }
    },

    /**
     * Compute control config.
     * @returns {Object}
     */
    controlsConfig() {
      const defaults = {
        hide: false,
        id: '',
        playPause: true,
        progressBar: false,
      }

      return {
        ...defaults,
        ...this.controls,
      }
    },

    /**
     * Compute control text based on state.
     * @returns {String}
     */
    controlText() {
      switch (this.videoPlayback.state) {
        case 'loading':
          return this.$string('accessibility.video.loading')

        case 'playing':
          return this.$string('accessibility.video.pause')

        case 'paused':
        default:
          return this.$string('accessibility.video.play')
      }
    },

    /**
     * Compute has progress bar.
     * @returns {Boolean}
     */
    hasProgressBar() {
      return this.controlsConfig?.progressBar && this.controlsConfig?.id
    },

    /**
     * Compute icon based on video state.
     * @returns {String}
     */
    icon() {
      switch (this.videoPlayback.state) {
        case 'loading':
          return 'icon-loading'

        case 'playing':
          return 'icon-pause'

        case 'paused':
        default:
          return 'icon-play'
      }
    },

    /**
     * Compute padding bottom.
     * - Used to apply a padding bottom to the main video element for aspect
     *   ratio.
     * - Also used to add a modifier class to apply `position: absolute` styling
     *   to the video and poster elements.
     * @returns {String|Null}
     */
    paddingBottom() {
      return this.aspectRatio ? computePaddingBottom(this.aspectRatio) : null
    },

    /**
     * Compute range slider config.
     * @returns {Object}
     */
    rangeSliderConfig() {
      if (this.videoPlayback.state === 'loading') {
        return {}
      }

      return {
        max: this.videoPlayback.duration,
        min: 0,
        step: 1,
      }
    },

    /**
     * Compute subtitles text.
     * @returns {String}
     */
    subtitlesText() {
      return this.displaySubtitles
        ? this.$string('accessibility.video.hide_subtitles')
        : this.$string('accessibility.video.show_subtitles')
    },

    /**
     * Compute a low-resolution poster image to pass to the video player.
     * - Poster is not seen by users as this component uses a custom poster
     *   using `<responsive-image>` component.
     * - Applying a `poster` attribute on the `<video>` element fixes an issue
     *   where the `<video>` element is sometimes detected as the LCP element by
     *   Lighthouse, which negatively impacts the LCP score.
     * @returns {String|Null}
     */
    videoPoster() {
      const imageUrl = this.image?.src || this.image?.url

      if (!imageUrl) {
        return null
      }

      return this.$formatImageUrl(
        this.image?.src || this.image?.url,
        { width: 16 },
      )
    },

    /**
     * Compute video sources.
     * - If sources prop is provided, processes sources array to grab the m3u8
     *   format source and the largest mp4 format source and return those
     *   sources.
     * - If the sources prop is empty, but the video prop has been provided,
     *   generates an array containing the video prop source, for compatibility.
     * @returns {Array}
     */
    videoSources() {
      if (!this.sources.length && this.video) {
        return [this.video]
      }

      const sources = [...this.sources]
      const outputSources = []

      const mp4Sources = sources.filter((source) => source.format === 'mp4')
      const m3u8Source = sources.find((source) => source.format === 'm3u8')

      const largestMp4Source = mp4Sources.reduce((previous, current) => {
        return current.width > previous.width ? current : previous
      }, mp4Sources[0])

      if (m3u8Source) {
        outputSources.push(m3u8Source)
      }

      if (largestMp4Source) {
        outputSources.push(largestMp4Source)
      }

      return outputSources
    },
  },

  watch: {

    /**
     * Watch display subtitles to show track.
     */
    displaySubtitles() {
      if (!this.displaySubtitles) {
        this.$refs.video.textTracks[0].mode = 'hidden'
        return
      }

      this.$refs.video.textTracks[0].mode = 'showing'
    },

    /**
     * Watch active overlay to pause/resume (if already playing).
     */
    getActiveOverlay() {
      if (this.getActiveOverlay) {
        if (this.videoPlayback.state === 'playing') {
          this.pauseVideo()
          this.overlayPaused = true
          return
        }

        this.overlayPaused = false
        return
      }

      /**
       * When overlay closes if video was previously playing resume it.
       */
      if (this.overlayPaused) {
        this.playVideo()
      }
    },

    /**
     * Watch has progress bar to load range slider when needed.
     */
    hasProgressBar() {
      this.loadRangeSlider()
    },

    /**
     * Watch active state to play video when slide containing it becomes active.
     * - Don't autoplay if prefers reduced motion.
     */
    isActive() {
      this.handleAutoplay()
    },

    /**
     * Watch parent state to update video playback state.
     * - This updates local state on the <video> element.
     */
    parentState() {
      if (!this.parentState) {
        return
      }

      if (this.parentState === 'playing') {
        this.playVideo()
        return
      }

      this.pauseVideo()
    },

    /**
     * Watch local state to emit update.
     */
    'videoPlayback.state'() {
      this.$emit('video-state-update', this.videoPlayback.state)
    },

    /**
     * Watch video playback time to emit update.
     */
    'videoPlayback.time'() {
      this.$emit('video-time-update', this.videoPlayback.time)
    },
  },

  mounted() {
    this.loadRangeSlider()

    /**
     * Reset any subtitles that browser has enabled by default.
     */
    if (!this.$refs.video.textTracks.length) {
      Object.keys(this.$refs.video.textTracks).forEach((_, index) => {
        this.$refs.video.textTracks[index].mode = 'hidden'
      })
    }

    this.handleAutoplay()
    this.setIntersectionObserver()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      loadComponent: 'components/load',
    }),

    /**
     * Load range slider component.
     */
    loadRangeSlider() {
      if (!this.hasProgressBar) {
        return
      }

      this.loadComponent('range-slider')
    },

    /**
     * Format progress bar tooltip text.
     * @param {Number} value - Provided time value.
     * @returns {String}
     */
    formatProgressBarTooltip(value) {
      const totalSeconds = Math.round(value)

      let seconds = totalSeconds % 60
      const minutes = String((totalSeconds - seconds) / 60)
      seconds = String(seconds).padStart(2, 0)

      return `${minutes}:${seconds}`
    },

    /**
     * Handle autoplay.
     * - Only check autoplay attribute on load, if you want to play the video
     *   after it's loaded then use the isActive prop.
     * - Never autoplay if intersection observer is enabled.
     */
    handleAutoplay() {
      if (
        'IntersectionObserver' in window &&
        this.intersectionObserver?.enable
      ) {
        return
      }

      if (
        (this.attributes?.autoplay || this.isActive) &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        this.playVideo()
        return
      }

      this.pauseVideo()
    },

    /**
     * Play video.
     */
    async playVideo() {
      try {
        await this.$refs?.video?.play()
        this.displayOverlay = false

      } catch (error) {
        cnvs.ReportError('Failed to play video', error)
      }
    },

    /**
     * Pause video.
     */
    async pauseVideo() {
      try {
        await this.$refs?.video?.pause()

      } catch (error) {
        cnvs.ReportError('Failed to pause video', error)
      }
    },

    /**
     * Set intersection observer.
     */
    setIntersectionObserver() {
      if (
        !this.intersectionObserver?.enable ||
        'IntersectionObserver' in window === false
      ) {
        return
      }

      setIntersectionObserver({
        callback: this.handleIntersection,
        element: this.$refs.video,
        rootMargin: this.intersectionObserver.rootMargin || '0px',
        threshold: this.intersectionObserver.threshold || 0.1,
      })
    },

    /**
     * Handle intersection.
     * @param {Array} entries - Intersection entries.
     */
    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          this.pauseVideo()
          return
        }

        this.playVideo()
      })
    },

    /**
     * Handle control and overlay click.
     */
    handleControlClick() {
      if (this.url) {
        location.href = this.url
        return
      }

      if (this.videoPlayback.state === 'loading') {
        return
      }

      if (this.videoPlayback.state === 'playing') {
        this.pauseVideo()
        return
      }

      this.playVideo()
    },

    /**
     * Handle progress bar interaction.
     * - On interaction disable time updates and pause video if currently
     *   playing to avoid progress bar jank.
     */
    handleProgressBarInteraction() {
      this.videoPlayback.updateTime = false

      if (this.videoPlayback.state !== 'playing') {
        return
      }

      this.videoPlayback.wasPlaying = true
      this.pauseVideo()
    },

    /**
     * Handle progress bar update.
     * - Resume playback if video was previously playing.
     * @param {Object} time - New time.
     */
    handleProgressBarUpdate(time) {
      this.videoPlayback.updateTime = true
      this.$refs.video.currentTime = time

      if (!this.videoPlayback.wasPlaying) {
        return
      }

      this.videoPlayback.wasPlaying = false
      this.playVideo()
    },

    /**
     * Handle video ready/time update.
     * @param {String} state - Video state.
     */
    handleVideoUpdate(state) {
      if (!this.videoPlayback.updateTime) {
        return
      }

      this.videoPlayback.duration = this.$refs.video?.duration || 0
      this.videoPlayback.state = state
      this.videoPlayback.time = this.$refs.video?.currentTime || 0
    },

    /**
     * Handle video playback end.
     */
    handleVideoEnd() {
      this.$emit('video-end')
    },
  },
}
</script>

<style lang="scss">
@import './video-player';
</style>
